import React, { useState } from 'react';
import { Users, TrendingUp, Eye, Calendar, BarChart3, X, Loader2, User, Mail, ChevronLeft, ChevronRight, Search, ChevronUp, ChevronDown, UserCheck, UserX, CheckCircle, Info, HelpCircle } from 'lucide-react';
import InfoTooltip from '../../../common/InfoToolTips';

// Section Header with Info Tooltip
const SectionHeader = ({ icon: Icon, title, description, color = "text-gray-800" }) => (
  <h4 className={`font-semibold ${color} mb-3 flex items-center`}>
    <Icon className="h-4 w-4 mr-2" />
    <span>{title}</span>
    <div className="ml-2">
      <InfoTooltip 
        title={title}
        description={description}
      />
    </div>
  </h4>
);

// Metric Row with Info Tooltip
const MetricRow = ({ label, value, tooltip, className = "" }) => (
  <div className="flex justify-between items-center">
    <div className="flex items-center">
      <span className={`text-sm text-gray-600 ${className}`}>{label}:</span>
      {tooltip && (
        <div className="ml-1">
          <InfoTooltip 
            title={label}
            description={tooltip}
          />
        </div>
      )}
    </div>
    <span className={`font-medium text-gray-900 ${className}`}>{value}</span>
  </div>
);

// Bookings Modal Component with Row Numbers, Pagination, Sorting, Search, and Invitation Tracking
const BookingsModal = ({ isOpen, onClose, eventId, bookingType }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // Filter state for invitation status
  const [inviteFilter, setInviteFilter] = useState('all'); // 'all', 'invited', 'not_invited'

  // Fetch bookings when modal opens
  React.useEffect(() => {
    if (isOpen && eventId) {
      console.log('Modal opened with eventId:', eventId, 'bookingType:', bookingType);
      setCurrentPage(1); // Reset to first page when modal opens
      setSearchTerm(''); // Reset search when modal opens
      setInviteFilter('all'); // Reset filter when modal opens
      setSortConfig({ key: null, direction: 'asc' }); // Reset sort when modal opens
      fetchBookings();
    }
  }, [isOpen, eventId]);

  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    setDebugInfo(null);

    const USE_MOCK = process.env.REACT_APP_USE_MOCK_API === 'true';

    // Use mock data directly if mock mode is enabled
    if (USE_MOCK) {
      try {
        const { MOCK_BOOKINGS } = await import('../../../../services/mockData');
        const mockBookings = MOCK_BOOKINGS[eventId] || [];
        setBookings(mockBookings);
        setDebugInfo({ apiUrl: 'mock', responseStatus: 200, dataReceived: { bookings: mockBookings }, bookingsCount: mockBookings.length });
      } catch (err) {
        setError('Failed to load mock bookings data');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const apiUrl = `${API_BASE}/campaigns/bookings/event/${eventId}`;
      const API_KEY = process.env.REACT_APP_PIPELINE_API_KEY || 'dev-key-12345';

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${API_KEY}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch bookings: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      setDebugInfo({
        apiUrl,
        responseStatus: response.status,
        dataReceived: data,
        bookingsCount: data.bookings ? data.bookings.length : 0
      });

      setBookings(data.bookings || []);
    } catch (err) {
      console.error('Fetch Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Search and filter bookings
  const filteredBookings = React.useMemo(() => {
    let filtered = bookings;
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(booking => {
        const contactId = (booking.contact_id || '').toString().toLowerCase();
        const email = (booking.email || '').toLowerCase();
        
        return contactId.includes(searchLower) || email.includes(searchLower);
      });
    }
    
    // Apply invitation filter (handling 1/0 instead of true/false)
    if (inviteFilter === 'invited') {
      filtered = filtered.filter(booking => booking.was_invited === 1);
    } else if (inviteFilter === 'not_invited') {
      filtered = filtered.filter(booking => booking.was_invited === 0);
    }
    
    return filtered;
  }, [bookings, searchTerm, inviteFilter]);

  // Sort bookings
  const sortedBookings = React.useMemo(() => {
    if (!sortConfig.key) return filteredBookings;
    
    return [...filteredBookings].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle different data types
      if (sortConfig.key === 'contact_id') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      } else if (sortConfig.key === 'was_invited') {
        aValue = a.was_invited === 1 ? 1 : (a.was_invited === 0 ? 0 : -1);
        bValue = b.was_invited === 1 ? 1 : (b.was_invited === 0 ? 0 : -1);
      } else {
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredBookings, sortConfig]);

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get sort icon for column headers
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="h-4 w-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-blue-600" />
      : <ChevronDown className="h-4 w-4 text-blue-600" />;
  };

  // Handle search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Handle invitation filter change
  const handleInviteFilterChange = (filterValue) => {
    setInviteFilter(filterValue);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setInviteFilter('all');
    setCurrentPage(1);
  };

  // Pagination calculations (use sorted/filtered data)
  const totalItems = sortedBookings.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = sortedBookings.slice(startIndex, endIndex);

  // Calculate invitation statistics (handling 1/0 instead of true/false)
  const invitationStats = React.useMemo(() => {
    const invitedBookings = bookings.filter(b => b.was_invited === 1);
    const notInvitedBookings = bookings.filter(b => b.was_invited === 0);
    const unknownInviteStatus = bookings.filter(b => b.was_invited === null || b.was_invited === undefined);
    
    return {
      total: bookings.length,
      invited: invitedBookings.length,
      notInvited: notInvitedBookings.length,
      unknown: unknownInviteStatus.length,
      invitedPercentage: bookings.length > 0 ? ((invitedBookings.length / bookings.length) * 100).toFixed(1) : '0.0'
    };
  }, [bookings]);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPrevPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // Generate page numbers for pagination display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      const end = Math.min(totalPages, start + maxVisible - 1);
      
      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages) {
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header — Gradient */}
        <div className="relative px-6 py-5 overflow-hidden" style={{ background: 'linear-gradient(135deg, #152451, #0093cf)' }}>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)' }} />
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white">Bookings for Event {eventId}</h3>
              {!loading && !error && bookings.length > 0 && (
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-white/70">
                    {searchTerm || inviteFilter !== 'all'
                      ? `${startIndex + 1}–${Math.min(endIndex, totalItems)} of ${totalItems} filtered (${bookings.length} total)`
                      : `${startIndex + 1}–${Math.min(endIndex, totalItems)} of ${totalItems} bookings`}
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="bg-white/15 text-white text-xs rounded-md px-2 py-1 border border-white/20 focus:outline-none"
                  >
                    {[5, 10, 25, 50, 100].map(n => <option key={n} value={n} className="text-darkblue-500">{n} per page</option>)}
                  </select>
                </div>
              )}
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 160px)' }}>
          <div className="p-6">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#0093cf' }} />
                <span className="ml-3 text-grey-600">Loading bookings…</span>
              </div>
            )}

            {error && (
              <div className="bg-pink-50 border border-pink-200 rounded-xl p-4">
                <p className="text-pink-800 font-semibold text-sm">Error loading bookings</p>
                <p className="text-pink-600 text-xs mt-1">{error}</p>
                <button onClick={fetchBookings} className="mt-3 px-4 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors" style={{ background: '#ed145b' }}>
                  Try Again
                </button>
              </div>
            )}

            {!loading && !error && bookings.length === 0 && (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-grey-300 mx-auto mb-4" />
                <p className="text-grey-600 font-semibold">No bookings found</p>
                <p className="text-grey-500 text-sm mt-1">No bookings recorded for event {eventId}</p>
              </div>
            )}

            {!loading && !error && bookings.length > 0 && (
              <div>
                {/* Summary cards — gradient */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  <div className="relative rounded-xl p-4 overflow-hidden" style={{ background: 'linear-gradient(135deg, #152451, #1e3a6e)' }}>
                    <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)' }} />
                    <div className="relative z-10">
                      <div className="text-[10px] font-semibold text-white/70 uppercase tracking-wide">Total Bookings</div>
                      <div className="text-2xl font-bold text-white">{bookings.length}</div>
                    </div>
                  </div>
                  <div className="relative rounded-xl p-4 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0093cf, #22b0e8)' }}>
                    <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)' }} />
                    <div className="relative z-10">
                      <div className="text-[10px] font-semibold text-white/70 uppercase tracking-wide">Unique Emails</div>
                      <div className="text-2xl font-bold text-white">{new Set(bookings.map(b => b.email).filter(Boolean)).size}</div>
                    </div>
                  </div>
                  <div className="relative rounded-xl p-4 overflow-hidden" style={{ background: 'linear-gradient(135deg, #009391, #00b8b5)' }}>
                    <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)' }} />
                    <div className="relative z-10">
                      <div className="text-[10px] font-semibold text-white/70 uppercase tracking-wide flex items-center gap-1"><UserCheck className="h-3 w-3" /> Invited</div>
                      <div className="text-2xl font-bold text-white">{invitationStats.invited} <span className="text-sm font-normal text-white/70">({invitationStats.invitedPercentage}%)</span></div>
                    </div>
                  </div>
                  <div className="relative rounded-xl p-4 overflow-hidden" style={{ background: 'linear-gradient(135deg, #f58025, #f99b4e)' }}>
                    <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)' }} />
                    <div className="relative z-10">
                      <div className="text-[10px] font-semibold text-white/70 uppercase tracking-wide flex items-center gap-1"><UserX className="h-3 w-3" /> Not Invited</div>
                      <div className="text-2xl font-bold text-white">{invitationStats.notInvited}{invitationStats.unknown > 0 && <span className="text-sm font-normal text-white/70"> +{invitationStats.unknown}</span>}</div>
                    </div>
                  </div>
                </div>

                {/* Search + filters */}
                <div className="mb-4 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                    <input
                      type="text" value={searchTerm} onChange={handleSearchChange}
                      placeholder="Search by Contact ID or Email…"
                      className="w-full pl-10 pr-10 py-2 border border-grey-200 rounded-lg text-sm bg-white placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
                    />
                    {searchTerm && (
                      <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="h-4 w-4 text-grey-400 hover:text-grey-600" /></button>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-grey-500">Status:</span>
                    {[
                      { key: 'all', label: `All (${bookings.length})`, activeStyle: 'background: linear-gradient(135deg, #152451, #1e3a6e)' },
                      { key: 'invited', label: `Invited (${invitationStats.invited})`, activeStyle: 'background: linear-gradient(135deg, #009391, #00b8b5)', icon: UserCheck },
                      { key: 'not_invited', label: `Not Invited (${invitationStats.notInvited + invitationStats.unknown})`, activeStyle: 'background: linear-gradient(135deg, #f58025, #f99b4e)', icon: UserX },
                    ].map(f => (
                      <button
                        key={f.key}
                        onClick={() => handleInviteFilterChange(f.key)}
                        className={`px-3 py-1.5 text-xs rounded-lg font-semibold transition-all duration-200 flex items-center gap-1 ${
                          inviteFilter === f.key ? 'text-white shadow-md' : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
                        }`}
                        style={inviteFilter === f.key ? { [f.activeStyle.split(':')[0].trim()]: f.activeStyle.split(':').slice(1).join(':').trim() } : {}}
                        {...(inviteFilter === f.key ? { style: Object.fromEntries(f.activeStyle.split(';').filter(Boolean).map(s => { const [k, ...v] = s.split(':'); return [k.trim(), v.join(':').trim()]; })) } : {})}
                      >
                        {f.icon && <f.icon className="h-3 w-3" />}
                        {f.label}
                      </button>
                    ))}
                    {(searchTerm || inviteFilter !== 'all') && (
                      <button onClick={clearAllFilters} className="text-xs text-grey-500 hover:text-pink-600 underline ml-1">Clear</button>
                    )}
                  </div>
                </div>

                {/* Table */}
                {totalItems > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-grey-200">
                    <table className="min-w-full divide-y divide-grey-200">
                      <thead>
                        <tr style={{ background: 'linear-gradient(135deg, #152451, #1e3a6e)' }}>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white/90 uppercase tracking-wide w-14">#</th>
                          {[
                            { key: 'contact_id', label: 'Contact ID' },
                            { key: 'email', label: 'Email' },
                            { key: 'was_invited', label: 'Status' },
                          ].map(col => (
                            <th key={col.key} className="px-4 py-3 text-left text-xs font-semibold text-white/90 uppercase tracking-wide cursor-pointer hover:bg-white/10 transition-colors" onClick={() => handleSort(col.key)}>
                              <div className="flex items-center justify-between">
                                <span>{col.label}</span>
                                {getSortIcon(col.key)}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-grey-100">
                        {currentBookings.map((booking, index) => {
                          const globalIndex = startIndex + index + 1;
                          const wasInvited = booking.was_invited;
                          return (
                            <tr key={booking.contact_id || index} className="hover:bg-grey-50 transition-colors">
                              <td className="px-4 py-3 text-xs font-medium text-grey-400 w-14">{globalIndex}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <User className="h-3.5 w-3.5 text-grey-400" />
                                  <button
                                    onClick={() => window.open(`https://app.gohighlevel.com/v2/location/G7ZOWCq78JrzUjlLMCxt/contacts/detail/${booking.contact_id}`, "_blank", "width=1000,height=800,scrollbars=yes,resizable=yes")}
                                    className="text-xs font-medium hover:underline" style={{ color: '#0093cf' }}
                                  >
                                    {booking.contact_id || `Contact ${globalIndex}`}
                                  </button>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <Mail className="h-3.5 w-3.5 text-grey-400" />
                                  <span className="text-xs text-darkblue-500">{booking.email || 'N/A'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {wasInvited === 1 ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#d0fffc', color: '#005f5e' }}>
                                    <CheckCircle className="h-3 w-3" /> Invited
                                  </span>
                                ) : wasInvited === 0 ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: '#fdead6', color: '#983e14' }}>
                                    <UserX className="h-3 w-3" /> Not Invited
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-grey-100 text-grey-500">
                                    Unknown
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Search className="h-8 w-8 text-grey-300 mx-auto mb-2" />
                    <p className="text-grey-500 text-sm">No bookings match your criteria</p>
                    <button onClick={clearAllFilters} className="mt-2 text-xs font-semibold hover:underline" style={{ color: '#0093cf' }}>Clear all filters</button>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between pt-4 border-t border-grey-200">
                    <span className="text-xs text-grey-500">
                      {startIndex + 1}–{Math.min(endIndex, totalItems)} of {totalItems}
                      {(searchTerm || inviteFilter !== 'all') && ` (filtered from ${bookings.length})`}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={goToFirstPage} disabled={currentPage === 1} className="px-2 py-1 text-xs font-medium text-grey-500 hover:text-darkblue-500 disabled:opacity-40 disabled:cursor-not-allowed">First</button>
                      <button onClick={goToPrevPage} disabled={currentPage === 1} className="p-1 text-grey-400 hover:text-darkblue-500 disabled:opacity-40 disabled:cursor-not-allowed"><ChevronLeft className="h-4 w-4" /></button>
                      {getPageNumbers().map((page, i) => (
                        <React.Fragment key={i}>
                          {page === '...' ? <span className="px-2 text-xs text-grey-400">…</span> : (
                            <button
                              onClick={() => goToPage(page)}
                              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${currentPage === page ? 'text-white shadow-sm' : 'text-grey-600 hover:bg-grey-100'}`}
                              style={currentPage === page ? { background: 'linear-gradient(135deg, #152451, #0093cf)' } : {}}
                            >{page}</button>
                          )}
                        </React.Fragment>
                      ))}
                      <button onClick={goToNextPage} disabled={currentPage === totalPages} className="p-1 text-grey-400 hover:text-darkblue-500 disabled:opacity-40 disabled:cursor-not-allowed"><ChevronRight className="h-4 w-4" /></button>
                      <button onClick={goToLastPage} disabled={currentPage === totalPages} className="px-2 py-1 text-xs font-medium text-grey-500 hover:text-darkblue-500 disabled:opacity-40 disabled:cursor-not-allowed">Last</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced ExpandedRowDetails with Invitation Summary and Info Tooltips
const ExpandedRowDetails = ({ row }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    eventId: null,
    bookingType: null
  });

  const handleBookingClick = (bookingType, eventId) => {
    const finalEventId = eventId || row.id;

    setModalState({
      isOpen: true,
      eventId: finalEventId,
      bookingType: bookingType
    });

  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      eventId: null,
      bookingType: null
    });
  };

  // Helper function to make booking numbers clickable with invitation context
  const BookingButton = ({ value, type, className = "", inviteInfo = null }) => {
    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleBookingClick(type, row.event || row.webinar_no);
    };
    
    return (
      <div className="text-right">
        <button
          onClick={handleClick}
          className={`font-medium hover:underline hover:text-blue-600 transition-colors cursor-pointer ${className} bg-yellow-100 border border-yellow-300 px-1 rounded block mb-1`}
          title={`Click to view ${type} bookings with invitation details`}
        >
          {value || '0'} 
        </button>
        {inviteInfo && (
          <div className="text-xs text-gray-500">
            <div className="flex items-center justify-end space-x-1">
              <UserCheck className="h-3 w-3 text-green-500" />
              <span>{inviteInfo.invited || 0}</span>
              <UserX className="h-3 w-3 text-orange-500 ml-1" />
              <span>{inviteInfo.notInvited || 0}</span>
            </div>
            <div className="text-gray-400">
              {inviteInfo.total > 0 ? `${((inviteInfo.invited / inviteInfo.total) * 100).toFixed(0)}% invited` : 'No data'}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Conversion funnel step data for gradient cards
  const funnelSteps = [
    { label: 'Invite → Reg', value: `${row.conversions?.inviteToReg || '0.0'}%`, from: '#92278f', to: '#b040ad' },
    { label: 'Reg → Attend', value: `${row.conversions?.regToAttend || '0.0'}%`, from: '#009391', to: '#00b8b5' },
    { label: 'Attend → Book', value: `${row.conversions?.attendToBook || '0.0'}%`, from: '#f58025', to: '#f99b4e' },
    { label: 'Book → Sale', value: `${(() => { const s = row.sales?.total || 0; const b = row.bookings?.total || 0; return b > 0 ? ((s / b) * 100).toFixed(1) : '0.0'; })()}%`, from: '#ed145b', to: '#f44c82' },
  ];

  return (
    <>
      <tr>
        <td colSpan="14" className="px-0 py-0">
          <div className="bg-grey-50 p-5 border-t border-grey-200">
            {/* ── Breakdown cards ─────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Invitations */}
              <div className="rounded-xl overflow-hidden">
                <div className="px-4 py-2.5" style={{ background: 'linear-gradient(135deg, #92278f, #b040ad)' }}>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-white/80" />
                    <span className="text-xs font-semibold text-white uppercase tracking-wide">Invitations</span>
                    <div className="ml-auto"><InfoTooltip title="Invitations Breakdown" description="Cold Cal, LinkedIn, Non-joiners, and Expansion invites." /></div>
                  </div>
                </div>
                <div className="bg-white p-4 space-y-2 border border-t-0 border-grey-200 rounded-b-xl">
                  <MetricRow label="Cold Cal" tooltip="Excludes duplicate emails for the same webinar." value={row.calls?.cold?.toLocaleString() || '0'} />
                  <MetricRow label="- LinkedIn" value={row.calls?.linkedin_invites?.toLocaleString() || '0'} />
                  <MetricRow label="- Non-joiners" value={row.calls?.non_joiners?.toLocaleString() || '0'} />
                  <MetricRow label="Expansion" value={row.calls?.expansion?.toLocaleString() || '0'} />
                  <div className="border-t border-grey-100 pt-2 flex justify-between items-center">
                    <span className="text-sm font-semibold text-darkblue-500">Total</span>
                    <span className="text-base font-bold" style={{ color: '#92278f' }}>{row.calls?.total?.toLocaleString() || row.invitations?.toLocaleString() || '0'}</span>
                  </div>
                </div>
              </div>

              {/* Registrations */}
              <div className="rounded-xl overflow-hidden">
                <div className="px-4 py-2.5" style={{ background: 'linear-gradient(135deg, #152451, #1e3a6e)' }}>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-white/80" />
                    <span className="text-xs font-semibold text-white uppercase tracking-wide">Registrations</span>
                    <div className="ml-auto"><InfoTooltip title="Registrations Breakdown" description="Cold Cal Yes/Maybe, LinkedIn, and Webinar Tool registrations." /></div>
                  </div>
                </div>
                <div className="bg-white p-4 space-y-2 border border-t-0 border-grey-200 rounded-b-xl">
                  <MetricRow label="Cold Cal Yes" value={row.registrations?.coldCalls || '0'} />
                  <MetricRow label="Cold Cal Maybe" value={row.registrations?.maybe || '0'} />
                  <MetricRow label="LinkedIn" value={row.registrations?.linkedin || '0'} />
                  <MetricRow label="Webinar Tool" value={row.registrations?.zoom || '0'} />
                  <div className="border-t border-grey-100 pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-darkblue-500">Total</span>
                      <span className="text-base font-bold" style={{ color: '#152451' }}>{row.registrations?.total || '0'}</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-[10px] text-grey-500">Conversion</span>
                      <span className="text-[10px] font-semibold" style={{ color: '#152451' }}>{row.conversions?.inviteToReg || '0.0'}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Attendees */}
              <div className="rounded-xl overflow-hidden">
                <div className="px-4 py-2.5" style={{ background: 'linear-gradient(135deg, #009391, #00b8b5)' }}>
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-white/80" />
                    <span className="text-xs font-semibold text-white uppercase tracking-wide">Attendees</span>
                    <div className="ml-auto"><InfoTooltip title="Attendees Breakdown" description="Cold Cal, Non-joiners, LinkedIn, and Webinar Tool attendees." /></div>
                  </div>
                </div>
                <div className="bg-white p-4 space-y-2 border border-t-0 border-grey-200 rounded-b-xl">
                  <MetricRow label="Cold Cal" value={row.attendees?.coldCalls || '0'} />
                  <MetricRow label="Non-joiners" value={row.attendees?.non_joiners || '0'} />
                  <MetricRow label="LinkedIn" value={row.attendees?.linkedin || '0'} />
                  <MetricRow label="Webinar Tool" value={row.attendees?.other || '0'} />
                  <div className="border-t border-grey-100 pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-darkblue-500">Total</span>
                      <span className="text-base font-bold" style={{ color: '#009391' }}>{row.attendees?.total || '0'}</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-[10px] text-grey-500">Attend/Reg</span>
                      <span className="text-[10px] font-semibold" style={{ color: '#009391' }}>{row.conversions?.regToAttend || '0.0'}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bookings & Sales */}
              <div className="rounded-xl overflow-hidden">
                <div className="px-4 py-2.5" style={{ background: 'linear-gradient(135deg, #f58025, #f99b4e)' }}>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-white/80" />
                    <span className="text-xs font-semibold text-white uppercase tracking-wide">Bookings & Sales</span>
                    <div className="ml-auto"><InfoTooltip title="Bookings & Sales" description="Discovery calls booked and sales results. Click total to view contact details." /></div>
                  </div>
                </div>
                <div className="bg-white p-4 space-y-2 border border-t-0 border-grey-200 rounded-b-xl">
                  <MetricRow label="Webinar" value={row.bookings?.webinar} />
                  <MetricRow label="Website" value={row.bookings?.site} />
                  <MetricRow label="Email/SMS" value={row.bookings?.email} />
                  <MetricRow label="Other" value={row.bookings?.other} />
                  <div className="border-t border-grey-100 pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-darkblue-500">Total Booked</span>
                      <BookingButton value={row.bookings?.total} type="All Sources" className="text-brand-600 font-bold" inviteInfo={row.bookings?.inviteBreakdown} />
                    </div>
                  </div>
                  <div className="border-t border-grey-100 pt-2 space-y-1.5">
                    <div className="text-[10px] font-semibold text-grey-500 uppercase tracking-wide">Sales</div>
                    <MetricRow label="Showed" value={row.sales?.show || '0'} />
                    <MetricRow label="No Show" value={row.sales?.noShow || '0'} />
                    <MetricRow label="Sales" value={row.sales?.total || '0'} />
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-sm font-semibold text-darkblue-500">Revenue</span>
                      <span className="text-base font-bold" style={{ color: '#f58025' }}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(row.sales?.revenue || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Conversion funnel strip ──────────────────────────────── */}
            <div className="mt-4 grid grid-cols-4 gap-2">
              {funnelSteps.map((step) => (
                <div key={step.label} className="relative rounded-lg p-3 text-center overflow-hidden" style={{ background: `linear-gradient(135deg, ${step.from}, ${step.to})` }}>
                  <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 50%)' }} />
                  <div className="relative z-10">
                    <div className="text-xl font-bold text-white">{step.value}</div>
                    <div className="text-[10px] font-medium text-white/80 uppercase tracking-wide">{step.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </td>
      </tr>

      {/* Enhanced Bookings Modal */}
      <BookingsModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        eventId={modalState.eventId}
        bookingType={modalState.bookingType}
      />
    </>
  );
};

export default ExpandedRowDetails;